import { DeliveryModel } from '@audibene/ta.utils-opportunity'
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { PartnerPortalDeliveryModel } from 'types'
import { useUser } from '../useUser'
import { useIsIgnoringDeliveryModel } from '../useIsIgnoringDeliveryModel'

interface Props {
  children: ReactNode
}

type DeliveryModelContext = {
  deliveryModel: PartnerPortalDeliveryModel | undefined
  setDeliveryModel: (deliveryModel: PartnerPortalDeliveryModel) => void
}

type DeliveryModelWithUnknown = keyof typeof DeliveryModel | 'UNKNOWN'

const defaultContext: DeliveryModelContext = {
  deliveryModel: DeliveryModel.TELEAUDIOLOGY,
  setDeliveryModel: () => undefined,
}

const context = createContext(defaultContext)
const { Provider } = context

const DELIVERY_MODEL_LOCAL_STORAGE_KEY = 'deliveryModel'

/** Default selection order: prefer Teleaudiology, then TA Light; fall back to Teleaudiology if user has neither. */
const PREFERRED_DEFAULT_ORDER: readonly PartnerPortalDeliveryModel[] = [
  DeliveryModel.TELEAUDIOLOGY,
  DeliveryModel.TA_LIGHT,
]

const DeliveryModelProvider = ({ children }: Props) => {
  const { allowedDeliveryModels, accountId } = useUser()
  const isIgnoringDeliveryModel = useIsIgnoringDeliveryModel()
  const isReady = Boolean(accountId)

  const storedDeliveryModel = useMemo(
    () => localStorage.getItem(DELIVERY_MODEL_LOCAL_STORAGE_KEY) as PartnerPortalDeliveryModel | null,
    [isReady]
  )
  const fallbackDeliveryModel = useMemo(() => {
    if (!allowedDeliveryModels?.length) return DeliveryModel.TELEAUDIOLOGY

    const preferred = PREFERRED_DEFAULT_ORDER.find((dm) => allowedDeliveryModels.includes(dm))

    return (preferred ?? DeliveryModel.TELEAUDIOLOGY) as PartnerPortalDeliveryModel
  }, [allowedDeliveryModels])

  const selectedDeliveryModel = useMemo(
    () =>
      (allowedDeliveryModels.includes(storedDeliveryModel as DeliveryModel)
        ? (storedDeliveryModel as DeliveryModel)
        : fallbackDeliveryModel ?? 'UNKNOWN') as DeliveryModelWithUnknown,
    [allowedDeliveryModels, fallbackDeliveryModel, storedDeliveryModel]
  )

  useEffect(
    () => setDeliveryModel(storedDeliveryModel ?? fallbackDeliveryModel),
    [fallbackDeliveryModel, isReady, storedDeliveryModel]
  )

  const [deliveryModel, setDeliveryModel] = useState<PartnerPortalDeliveryModel | undefined>(
    selectedDeliveryModel && selectedDeliveryModel !== 'UNKNOWN'
      ? (selectedDeliveryModel as PartnerPortalDeliveryModel)
      : defaultContext.deliveryModel
  )

  const setDeliveryModelProxy = useCallback(
    (nextDeliveryModel: PartnerPortalDeliveryModel) => {
      localStorage.setItem(DELIVERY_MODEL_LOCAL_STORAGE_KEY, nextDeliveryModel)
      setDeliveryModel(nextDeliveryModel)
    },
    [localStorage, setDeliveryModel]
  )

  const value = useMemo(
    () => ({
      deliveryModel: isIgnoringDeliveryModel ? undefined : deliveryModel,
      setDeliveryModel: setDeliveryModelProxy,
    }),
    [deliveryModel, isIgnoringDeliveryModel, setDeliveryModelProxy]
  )

  return <Provider value={value}>{children}</Provider>
}

const useDeliveryModel = () => useContext(context)

export { DeliveryModelProvider, useDeliveryModel }
